import { DatePicker } from "@/common/components/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { isoLocalDate } from "@/features/cost-allocations/utils/formatPeriod";
import { X } from "lucide-react";
import type { ReceivedInvoicePipelineStatus } from "../api/receivedInvoicesApi";
import { PIPELINE_STATUS_LABEL } from "./InvoicePills";

export type ReceivedInvoicesUiFilters = {
  status: ReceivedInvoicePipelineStatus | ""; // "" = todos
  from: string; // YYYY-MM-DD ó ""
  to: string;
};

type Props = {
  filters: ReceivedInvoicesUiFilters;
  onChange: (next: ReceivedInvoicesUiFilters) => void;
};

const ALL_STATUSES = "__all__";

const STATUS_ORDER: ReceivedInvoicePipelineStatus[] = [
  "ready",
  "partially_assigned",
  "assigned",
  "quarantined",
  "received",
  "dismissed",
];

// El filtro habla en plural ("mostrame los descartados"); la pill de la card
// usa el label compartido en singular ("Descartado"). "Todos" NO incluye los
// dismissed — el backend los excluye por defecto y solo los devuelve con
// ?status=dismissed.
const FILTER_LABEL_OVERRIDE: Partial<Record<ReceivedInvoicePipelineStatus, string>> = {
  dismissed: "Descartados",
};

export function ReceivedInvoicesFilters({ filters, onChange }: Props) {
  const fromDate = filters.from ? parseDate(filters.from) : undefined;
  const toDate = filters.to ? parseDate(filters.to) : undefined;

  const hasAny = filters.status !== "" || filters.from !== "" || filters.to !== "";

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-app-border bg-app-surface p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-medium text-[13px] text-app-ink-2">Filtros</h2>
        {hasAny && (
          <button
            type="button"
            onClick={() => onChange({ status: "", from: "", to: "" })}
            className="inline-flex items-center gap-1 text-[12px] text-app-ink-3 hover:text-app-ink-1"
          >
            <X size={12} />
            Limpiar
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="filter-status" className="font-medium text-[12px] text-app-ink-3">
            Estado
          </label>
          <Select
            value={filters.status === "" ? ALL_STATUSES : filters.status}
            onValueChange={(v) =>
              onChange({
                ...filters,
                status: v === ALL_STATUSES ? "" : (v as ReceivedInvoicePipelineStatus),
              })
            }
          >
            <SelectTrigger id="filter-status" className="h-10 w-full text-app-ink-1">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STATUSES}>Todos</SelectItem>
              {STATUS_ORDER.map((s) => (
                <SelectItem key={s} value={s}>
                  {FILTER_LABEL_OVERRIDE[s] ?? PIPELINE_STATUS_LABEL[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="filter-from" className="font-medium text-[12px] text-app-ink-3">
            Desde
          </label>
          <DatePicker
            id="filter-from"
            value={fromDate}
            onChange={(d) => onChange({ ...filters, from: d ? isoLocalDate(d) : "" })}
            maxDate={toDate}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="filter-to" className="font-medium text-[12px] text-app-ink-3">
            Hasta
          </label>
          <DatePicker
            id="filter-to"
            value={toDate}
            onChange={(d) => onChange({ ...filters, to: d ? isoLocalDate(d) : "" })}
            minDate={fromDate}
          />
        </div>
      </div>
    </div>
  );
}

function parseDate(iso: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m?.[1] || !m[2] || !m[3]) return undefined;
  const y = Number.parseInt(m[1], 10);
  const month = Number.parseInt(m[2], 10);
  const day = Number.parseInt(m[3], 10);
  // El regex acepta 2026-99-99; confirmamos que el Date construido no hizo
  // rollover antes de devolverlo (mismo guard que CostAllocationsFilters).
  const d = new Date(y, month - 1, day);
  if (d.getFullYear() !== y || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return undefined;
  }
  return d;
}
